import type { Task } from '@dyspatch/core';
import { ExternalLink } from 'lucide-react';
import { prNumber, TaskKey } from '@/components/common/bits';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { mergeOrder } from '@/lib/merge-order';

/**
 * Which of a job's open pull requests may merge first (issue #24).
 *
 * The hub never merges, so this is advice rather than a queue: the order is
 * the plan's own dependency order, restricted to the PRs still open, and a
 * person is free to ignore it. It exists at all because nothing else on this
 * page answers "which of these five do I click first", and getting that wrong
 * is the one way a green PR breaks the base branch.
 *
 * Nothing here is drawn once nothing is open: a job between reviews has no
 * order to show, and "the plan" above already says so.
 */
export function MergeOrder({ tasks }: { tasks: readonly Task[] }) {
  const ordered = mergeOrder(tasks);
  if (ordered.length === 0) return null;

  return (
    <Section>
      <SectionHead title="Merge order" meta={`${ordered.length} open`} />
      <Surface>
        <ol className="divide-line divide-y">
          {ordered.map((task, index) => (
            <li key={task.id} className="flex items-center gap-2.5 px-gutter py-2 text-sm">
              <span className="text-fg-3 tnum w-4 shrink-0 text-right">{index + 1}.</span>
              <TaskKey>{task.key}</TaskKey>
              <span className="min-w-0 flex-1 truncate">{task.title}</span>
              {task.prUrl ? (
                <a
                  href={task.prUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-fg-3 hover:text-gate inline-flex shrink-0 items-center gap-1 text-xs transition-colors duration-100"
                >
                  PR{prNumber(task.prUrl)}
                  <ExternalLink className="size-3" aria-hidden />
                </a>
              ) : null}
            </li>
          ))}
        </ol>
      </Surface>
    </Section>
  );
}
